// The co-driver's tablet (Adam's request): the same upcoming pace notes as the HUD strip (same arrow
// geometry, grade labels, modifiers and caution colours from ui/pacenotes.ts), drawn into a canvas
// that becomes the tablet screen's texture. Redrawn only when the visible notes change.
import type { PaceNote } from '../codriver/notes';
import { arrowPath, cautionLevel, gradeLabel, hazardLabel, modifiers } from './pacenotes';

const W = 512;
const H = 346;
const INK = '#0b0d10';
const TEXT = '#f2f4f6';
const MUTED = '#9aa5b1';
const ACCENT = '#ff5a14';
const BAD = '#ff4d4d';
const FONT = "'Barlow Condensed', 'Arial Narrow', sans-serif";

export class TabletNotes {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  /** Path2D per arrow (grade + direction), built once. */
  private readonly paths = new Map<string, [Path2D, Path2D]>();
  private fontsReady = false;
  /** Set when the next draw must happen even if the notes did not change (fonts arrived). */
  dirty = true;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = W;
    this.canvas.height = H;
    this.ctx = this.canvas.getContext('2d')!;
    void document.fonts?.ready.then(() => {
      this.fontsReady = true;
      this.dirty = true;
    });
  }

  private arrow(n: PaceNote): [Path2D, Path2D] {
    const k = `${n.grade}:${n.dir}`;
    let p = this.paths.get(k);
    if (!p) {
      const [body, head] = arrowPath(n.grade!, n.dir!).split('|');
      p = [new Path2D(body), new Path2D(head)];
      this.paths.set(k, p);
    }
    return p;
  }

  /** Draw up to three notes (the next one first and largest). */
  draw(notes: readonly PaceNote[]): void {
    this.dirty = !this.fontsReady;
    const c = this.ctx;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.fillStyle = INK;
    c.fillRect(0, 0, W, H);
    // Header: a thin accent bar and the app label, like a road-book app.
    c.fillStyle = ACCENT;
    c.fillRect(0, 0, W, 6);
    c.fillStyle = MUTED;
    c.font = `700 22px ${FONT}`;
    c.textBaseline = 'middle';
    c.textAlign = 'left';
    c.fillText('PACE NOTES', 16, 28);
    if (!notes.length) {
      c.fillStyle = '#2a323b';
      c.font = `800 italic 64px ${FONT}`;
      c.textAlign = 'center';
      c.fillText('—', W / 2, H / 2 + 20);
      return;
    }
    // Tiles: the next note wide on the left, the following two stacked on the right.
    const tiles: [number, number, number, number][] = [
      [12, 52, 268, 282],
      [292, 52, 208, 134],
      [292, 200, 208, 134],
    ];
    notes.slice(0, 3).forEach((n, i) => {
      const [x, y, w, h] = tiles[i];
      this.tile(n, x, y, w, h, i === 0);
      // Link to the following note ("into" / "and").
      if (i < notes.length - 1 && i < 2 && n.link) {
        const label = n.link === 'into' ? 'INTO' : n.link === 'and' ? 'AND' : String(n.link).toUpperCase();
        c.font = `800 italic 20px ${FONT}`;
        c.fillStyle = TEXT;
        c.textAlign = 'center';
        const lx = i === 0 ? 286 : 396;
        const ly = i === 0 ? 46 : 193;
        c.fillStyle = INK;
        c.fillRect(lx - 30, ly - 11, 60, 22);
        c.fillStyle = ACCENT;
        c.fillText(label, lx, ly);
      }
    });
  }

  private tile(n: PaceNote, x: number, y: number, w: number, h: number, next: boolean): void {
    const c = this.ctx;
    const caution = cautionLevel(n);
    c.fillStyle = caution >= 2 ? '#3c0808' : next ? '#151a20' : '#11151a';
    c.fillRect(x, y, w, h);
    c.fillStyle = caution >= 2 ? BAD : caution === 1 ? ACCENT : next ? TEXT : MUTED;
    c.fillRect(x, y, w, next ? 7 : 5);
    const ink = caution === 1 ? ACCENT : TEXT;
    const mods = modifiers(n);
    const modsH = mods.length ? (next ? 34 : 26) : 0;
    if (n.kind === 'corner' && n.grade && n.dir) {
      // Arrow in a square on the left, grade on the right.
      const box = Math.min(h - modsH - 16, w * 0.62);
      const ax = x + 8;
      const ay = y + 10 + (h - modsH - 10 - box) / 2;
      const [body, head] = this.arrow(n);
      c.save();
      c.translate(ax, ay);
      c.scale(box / 100, box / 100);
      c.strokeStyle = ink;
      c.lineWidth = 9;
      c.lineCap = 'square';
      c.lineJoin = 'miter';
      c.stroke(body);
      c.fillStyle = ink;
      c.fill(head);
      c.lineWidth = 3;
      c.stroke(head);
      c.restore();
      const g = gradeLabel(n) + (n.refine === 'plus' ? '+' : n.refine === 'minus' ? '−' : '');
      c.fillStyle = TEXT;
      c.textAlign = 'right';
      c.textBaseline = 'top';
      const size = next ? (g.length > 2 ? 64 : 104) : g.length > 2 ? 44 : 64;
      c.font = `800 italic ${size}px ${FONT}`;
      c.fillText(g, x + w - 10, y + 14);
    } else {
      c.fillStyle = TEXT;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      const label = hazardLabel(n);
      let size = next ? 58 : 40;
      c.font = `800 italic ${size}px ${FONT}`;
      while (size > 18 && c.measureText(label).width > w - 20) {
        size -= 4;
        c.font = `800 italic ${size}px ${FONT}`;
      }
      c.fillText(label, x + w / 2, y + (h - modsH) / 2 + 4);
    }
    if (mods.length) {
      c.fillStyle = ACCENT;
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      const text = mods.join(' · ');
      let size = next ? 26 : 19;
      c.font = `700 ${size}px ${FONT}`;
      while (size > 12 && c.measureText(text).width > w - 12) {
        size -= 2;
        c.font = `700 ${size}px ${FONT}`;
      }
      c.fillText(text, x + w / 2, y + h - modsH / 2 - 4);
    }
  }
}
